<template>
  <div :class="classes">
    <h2
      v-if="label.length > 0"
      class="text-md mb-2">
      {{ label }}
    </h2>
    <Combobox
      ref="cb"
      v-model="selected"
      nullable>
      <div class="relative">
        <div
          class="relative flex items-center w-full cursor-default overflow-hidden rounded-lg bg-white text-left
          focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-300 sm:text-sm"
          :class="{'ring-1 ring-black ring-opacity-20': borders}">
          <slot
            v-if="selected?.image"
            name="image"
            :selected="selected">
            <img
              v-if="selected"
              :src="selected.image"
              alt=""
              class="flex-shrink-0 ml-4 rounded-sm h-4 w-6">
          </slot>
          <ComboboxInput
            class="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-600 focus:ring-0"
            :display-value="() => selected ? (typeof selected.user === 'object' ? selected.user.name : (typeof selected.symbol === 'string' ? selected.id : selected.name)) : query"
            :placeholder="placeholderName"
            autocomplete="off"
            @focus="cbBtn.el.click()"
            @change="query = $event.target.value" />
          <ComboboxButton
            ref="cbBtn"
            class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-600"
              aria-hidden="true" />
          </ComboboxButton>
        </div>
        <TransitionRoot
          leave="transition ease-in duration-100"
          leave-from="opacity-100"
          leave-to="opacity-0">
          <ComboboxOptions
            ref="cbOptions"
            class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-20 focus:outline-none sm:text-sm z-10">
            <div
              v-if="loading"
              class="relative cursor-disabled select-none">
              <svg
                role="status"
                class="w-4 h-4 m-2 text-gray-200 animate-spin dark:text-gray fill-blue"
                viewBox="0 0 100 101"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill" />
              </svg>
            </div>
            <template v-else>
              <div
                v-if="query === ''"
                class="relative flex justify-between cursor-default select-none py-2 px-3 text-gray-400">
                <slot name="type-to-search">
                  Start typing to search.
                </slot>
              </div>
              <div
                v-else-if="filteredResult.length === 0"
                class="relative cursor-default select-none py-2 px-4 text-gray-600">
                <slot name="no-result">
                  No result found.
                </slot>
              </div>
              <ComboboxOption
                v-for="item in filteredResult"
                :key="item.id"
                v-slot="{ selected, active }"
                as="template"
                :value="item"
                :static="false">
                <li
                  class="relative flex flex-row cursor-default items-center select-none pl-3 py-2 pr-4"
                  :class="{
                    'bg-primary text-white': active,
                    'text-gray-600': !active,
                    'exists': item.exists,
                  }">
                  <slot
                    :item="item"
                    :selected="selected"
                    :active="active">
                    <span
                      class="block truncate"
                      :class="{ 'font-medium': selected, 'font-normal': !selected }">
                      <img
                        :src="item.image"
                        alt=""
                        class="flex-shrink-0 rounded-sm h-4 w-6 mr-3">
                    </span>

                    <span
                      class="block truncate mr-3 font-medium "
                      :class="{ 'font-gray-900': selected, 'font-normal': !selected }">
                      {{ item.name }}
                    </span>

                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3"
                      :class="{ 'text-font': active, 'text-primary': !active }">
                      <CheckIcon
                        class="h-5 w-5"
                        aria-hidden="true"
                        :class="{ 'text-white': active, 'text-primary': !active }" />
                    </span>
                  </slot>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
  </div>
  <slot
    name="after"
    :selected="selected"
    :query="query" />
</template>

<script setup>
import {
  computed,
  onMounted, ref, watch,
} from 'vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue';
import {
  CheckIcon, ChevronUpDownIcon,
} from '@heroicons/vue/24/solid';

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: null,
  },
  classes: {
    type: String,
    required: false,
    default: '',
  },
  apiValue: {
    type: String,
    required: true,
    default: '',
  },
  initialPopulation: {
    type: String,
    required: false,
    default: '',
  },
  preExistingValue: {
    type: Array,
    required: false,
    default: [],
  },
  placeholderValue: {
    type: String,
    required: false,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  borders: {
    type: Boolean,
    required: false,
    default: true,
  },
});
const placeholderName = props.placeholderValue;
const preExistingValue = computed(() => props.preExistingValue);
const selected = ref(props.modelValue);
const result = ref([]);
const query = ref('');
const loading = ref(false);
const cbBtn = ref();
const cb = ref();
let timer;

const filteredResult = computed(() => {
  if (preExistingValue.value.length > 0) {
    return result.value
      .map((item) => ({
        ...item,
        exists: preExistingValue.value.map((l) => l.id).includes(item.id),
      }));
  }

  return result.value;
});

const emit = defineEmits({ update: (_value) => true });

watch(selected, () => {
  cbBtn.value.el.click();
  emit('update:modelValue', selected.value);
});

onMounted(() => {
  if (props.initialPopulation.length > 0) {
    loading.value = true;
    axios
      .get(props.initialPopulation)
      .then((response) => {
        result.value = response.data;
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }
});

function search() {
  loading.value = true;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  timer = setTimeout(() => {
    axios
      .get(props.apiValue + query.value)
      .then((response) => {
        result.value = response.data;
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }, 500);
}

watch(query, () => {
  search();
});

function resetSelected() {
  query.value = '';
  selected.value = null;
}

function setSelected(value) {
  query.value = '';
  selected.value = value;
}

defineExpose({
  resetSelected, setSelected,
});

</script>
